import { Box, IconButton, Stack } from '@mui/material';
import React, { useEffect } from 'react';
import Dropdown from '../ui/dropdown';
import Button from '../ui/button';
import { ChevronLeft16Filled, ChevronRight16Filled } from '@fluentui/react-icons';

const iconButtonStyle = {
  border: '1px solid var(--gray-300)', 
  borderRadius: '10px', 
  backgroundColor: 'var(--white)',
  color: 'var(--gray-500)',
  height: '40px',
  width: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ':hover': {
    backgroundColor: 'var(--gray-50)',
    borderColor: 'var(--gray-300)',
  },
  '&:disabled': {
    cursor: 'default',
    pointerEvents: 'none',
    color: 'var(--gray-300)', 
    backgroundColor: 'var(--gray-100)',
    borderColor: 'var(--gray-200)',
    '&:active': {
      backgroundColor: 'var(--gray-300)',
      border: '1px solid var(--gray-200)',
    },
  },
};

/**
 * Renders a set of pagination controls for navigating through pages of data.
 * It provides buttons for previous and next page navigation, displays the current page and total pages,
 * and allows the user to select the number of rows displayed per page, including an "All" option.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {number} props.currentPage - The current page number.
 * @param {number} props.totalPages - The total number of pages.
 * @param {number|string} props.rowsPerPage - The number of rows displayed per page ("All" for all rows).
 * @param {Function} props.onPreviousPage - Callback function to handle previous page navigation.
 * @param {Function} props.onNextPage - Callback function to handle next page navigation.
 * @param {Function} props.onRowsPerPageChange - Callback function to handle changing the number of rows per page.
 *
 * @returns {JSX.Element} A rendered set of pagination controls.
 */
const PaginationControls = ({
  currentPage,
  totalPages,
  rowsPerPage,
  onPreviousPage,
  onNextPage,
  onRowsPerPageChange,
}) => {
  // Adjust the current page if it exceeds the total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      onPreviousPage();
    }
  }, [currentPage, totalPages, onPreviousPage]);

  const rowsPerPageOptions = [
    { value: 5, label: '5 per page'},
    { value: 10, label: '10 per page' },
    { value: 20, label: '20 per page' },
    { value: 'All', label: 'All' },
  ];

  return (
    <Stack display="flex" direction="row" alignItems="center" justifyContent="center" gap={1}>
      {/* Rows per page dropdown */}
      <Box width="130px" display="flex" justifyContent="center">
        <Dropdown
          value={rowsPerPage}
          onChange={(e) =>
            onRowsPerPageChange(
              e.target.value === 'All' ? 'All' : Number(e.target.value)
            )
          }
          options={rowsPerPageOptions}
          sx={{
            height: "40px", 
          }}
        />
      </Box>

      {/* Previous button */}
      <IconButton
        onClick={onPreviousPage}
        disabled={currentPage === 1}
        sx={iconButtonStyle}
      >
        <ChevronLeft16Filled />
      </IconButton>

      {/* Page number display */}
      <Button
        variant="outlined"
        sx={{
          pointerEvents: 'none',
          fontWeight: '400px',
          height: '40px',
        }}
      >
        Page {rowsPerPage === 'All' ? 1 : currentPage} of{' '}
        {rowsPerPage === 'All' ? 1 : totalPages || 1}
      </Button>

      {/* Next button */}
      <IconButton
        onClick={onNextPage}
        disabled={currentPage === totalPages || totalPages === 0}
        sx={iconButtonStyle}
      >
        <ChevronRight16Filled />
      </IconButton>
    </Stack>
  );
};

export default PaginationControls;
